import { CATEGORIES, questions } from '../data/questions';

const STRENGTH_THRESHOLD = 70;
const RISK_THRESHOLD = 50;

const CATEGORY_STRENGTHS = {
  governance: 'Mature AI Governance',
  dlp: 'Strong Data Protection',
  adoption: 'Strong AI Adoption',
  agents: 'Advanced AI Agent Strategy',
  security: 'Good Security Practices',
};

const CATEGORY_RISKS = {
  governance: 'No AI Governance',
  dlp: 'Weak DLP',
  adoption: 'Limited AI Adoption',
  agents: 'No AI Agent Strategy',
  security: 'Insufficient Security Controls',
};

const CATEGORY_RECOMMENDATIONS = {
  governance: 'Implement a formal AI Governance policy with clear ownership and monitoring.',
  dlp: 'Deploy AI-aware Data Loss Prevention to catch sensitive data before it reaches AI tools.',
  adoption: 'Formalize an enterprise AI adoption roadmap to move beyond ad-hoc usage.',
  agents: 'Introduce AI Agent governance covering access, actions, and audit trails.',
  security: 'Strengthen AI-specific security controls and enable continuous monitoring.',
};

const CHALLENGE_RECOMMENDATIONS = {
  Security: 'Prioritize AI-specific threat detection and access controls.',
  'Data Privacy': 'Adopt AI-aware DLP and data classification for AI workflows.',
  Compliance: 'Map AI usage against regulatory requirements and build an audit trail.',
  'AI Agents': 'Establish an AI Agent governance framework before scaling further.',
  Integration: 'Standardize on a centralized AI platform to simplify integration.',
  Cost: 'Consolidate AI tooling to reduce redundant spend and licensing.',
  'Employee Adoption': 'Invest in AI training and champions to drive adoption.',
};

function uniqueTop(list, count) {
  return [...new Set(list)].slice(0, count);
}

/**
 * Builds the strengths / risks / recommendations lists shown on the
 * result dashboard. `answers` is the same raw answers map passed to
 * calculateScores. `otherTexts` is the optional `{ [questionId]: string }`
 * map of free text entered whenever a question's "Others" option was
 * selected (see Assessment.jsx / QuestionCard.jsx).
 */
export function buildInsights(categoryScores, answers, otherTexts = {}) {
  const ranked = CATEGORIES.map((c) => ({ ...c, score: categoryScores[c.key] })).sort(
    (a, b) => b.score - a.score
  );

  let strengths = ranked
    .filter((c) => c.score >= STRENGTH_THRESHOLD)
    .slice(0, 3)
    .map((c) => CATEGORY_STRENGTHS[c.key]);

  if (strengths.length === 0 && ranked.length > 0) {
    strengths = [CATEGORY_STRENGTHS[ranked[0].key]];
  }

  let risks = ranked
    .filter((c) => c.score < RISK_THRESHOLD)
    .sort((a, b) => a.score - b.score)
    .map((c) => CATEGORY_RISKS[c.key]);

  const q5Answer = answers.q5;
  const q5UnrestrictedSelected = Array.isArray(q5Answer) ? q5Answer.includes(0) : q5Answer === 0;
  if (q5UnrestrictedSelected) {
    risks = ['Public AI Usage Without Restrictions', ...risks];
  }

  const recommendations = ranked
    .filter((c) => c.score < STRENGTH_THRESHOLD)
    .sort((a, b) => a.score - b.score)
    .map((c) => CATEGORY_RECOMMENDATIONS[c.key]);

  const q9 = questions.find((q) => q.id === 'q9');
  const challengeAnswer = answers.q9;
  const challengeIndex = Array.isArray(challengeAnswer) ? challengeAnswer[0] : challengeAnswer;
  const challenge = q9 && Number.isInteger(challengeIndex) ? q9.options[challengeIndex] : null;

  let leadRecommendation = null;
  if (challenge && CHALLENGE_RECOMMENDATIONS[challenge]) {
    leadRecommendation = CHALLENGE_RECOMMENDATIONS[challenge];
  } else if (challenge && challenge.trim().toLowerCase() === 'others' && otherTexts.q9) {
    // User described their own biggest challenge in the "Others" text box —
    // surface it directly rather than dropping it silently.
    leadRecommendation = `Address your stated top challenge: "${otherTexts.q9.trim()}".`;
  }

  const orderedRecommendations = leadRecommendation
    ? [leadRecommendation, ...recommendations]
    : recommendations;

  return {
    strengths: uniqueTop(strengths, 3),
    risks: uniqueTop(risks, 4),
    recommendations: uniqueTop(orderedRecommendations, 5),
  };
}

export function buildExecutiveSummary(overallScore, categoryScores, maturityLabel) {
  const ranked = CATEGORIES.map((c) => ({ ...c, score: categoryScores[c.key] })).sort(
    (a, b) => b.score - a.score
  );
  const strongest = ranked[0];
  const weakest = ranked[ranked.length - 1];

  return (
    `Your organization scored ${overallScore}/100, placing it in the ${maturityLabel} category. ` +
    `${strongest.label} is your strongest area, reflecting solid foundations already in place. ` +
    `The greatest opportunity lies in ${weakest.label.toLowerCase()}, where strengthening policy, ` +
    `tooling, and oversight will reduce enterprise risk and accelerate safe AI adoption.`
  );
}
